import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Grid, CircularProgress, Alert } from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OrganisationDevicesTable from "../components/Tables/OrganisationDevicesTable";
import PageTitle from "../components/Layout/PageTitle";
import CardInfo from "../components/Cards/CardInfo";
import { Person, Router } from "@mui/icons-material";
import OrganisationMembersTable from "../components/Tables/OrganisationMembers";
import AddMemberButton from "../components/Buttons/AddMember";
import DeleteOrganisationButton from "../components/Buttons/DeleteOrganisation";
import ChangeOrganisationOwnerButton from "../components/Buttons/ChangeOrganisationOwner";
import ChangeOrganisationNameButton from "../components/Buttons/ChangeOrganisationName";
import OrganisationActions from "../components/Tables/OrganisationActions";
import OrganisationContacts from "../components/Tables/OrganisationContacts";
import OrganisationGeofences from "../components/Tables/OrganisationGeofences";
import { useOrganisation } from "../hooks/useOrganisations";
import { useDevices } from "../hooks/useDevices";
import { useUsers } from "../hooks/useUsers";

function Organisation() {
  const params = useParams();
  const navigate = useNavigate();
  const organisationId = parseInt(params.id);

  const {
    data: organisation,
    isLoading: orgLoading,
    error: orgError,
  } = useOrganisation(organisationId);
  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useDevices();
  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();

  const isLoading = orgLoading || devicesLoading || usersLoading;
  const hasError = orgError || devicesError || usersError;

  // Memoized calculations
  const { devicesFiltered, ownerEmail } = useMemo(() => {
    const filtered =
      devices?.filter((device) => device.organisation_id === organisationId) ||
      [];
    const owner = users?.find((user) => user.id === organisation?.owner_id);

    return {
      devicesFiltered: filtered,
      ownerEmail: owner?.email || "N/A",
    };
  }, [devices, users, organisation, organisationId]);

  if (isLoading) {
    return (
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12} textAlign="center">
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (hasError) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Alert severity="error">
            Failed to load organisation data:{" "}
            {(orgError || devicesError || usersError)?.message ||
              "Unknown error"}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1}>
      <PageTitle title={organisation?.name || "Organisation"}>
        <ChangeOrganisationNameButton
          organisationId={params.id}
          currentName={organisation?.name}
        />
        <ChangeOrganisationOwnerButton
          organisationId={params.id}
          currentOwnerId={organisation?.owner_id}
        />
        <AddMemberButton organisation_id={params.id} />
        <DeleteOrganisationButton organisation_id={params.id} />
      </PageTitle>
      <Grid item xs={12} md={6}>
        <CardInfo
          avatar={<Person fontSize="large" />}
          title="Owner"
          hasEvent={true}
          event={() => {
            navigate("/user/" + organisation?.owner_id);
          }}
          value={ownerEmail}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CardInfo
          avatar={<Router fontSize="large" />}
          title="Total Devices"
          value={devicesFiltered.length || 0}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <OrganisationDevicesTable
          organisationID={params.id}
          devices={devicesFiltered}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <OrganisationMembersTable organisation_id={params.id} />
      </Grid>
      <Grid item xs={12} md={12}>
        <OrganisationActions organisation_id={params.id} />
      </Grid>
      <Grid item xs={12} md={12}>
        <OrganisationContacts organisation_id={params.id} />
      </Grid>
      <Grid item xs={12} md={12}>
        <OrganisationGeofences organisation_id={params.id} />
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Organisation);
